import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui';

/** Worker agents report "agent <build>"; container workers report a plain version. */
const AGENT_PREFIX = 'agent ';

export const isAgentVersion = (version: string) => version.startsWith(AGENT_PREFIX);

/** "agent 89b05540aeff…" → "agent 89b0554"; anything else → "v0.1.0". */
export function workerVersionLabel(version: string): string {
  if (!version) return '';
  return isAgentVersion(version) ? `agent ${version.slice(AGENT_PREFIX.length, AGENT_PREFIX.length + 7)}` : `v${version}`;
}

/** How long a fetched version.txt is trusted: a page left open must notice new builds (and updated workers must not keep showing "update available"). */
const LATEST_TTL_MS = 60_000;
let cached: { at: number; value: Promise<string | null> } | null = null;

function latestWorkerVersion(): Promise<string | null> {
  if (!cached || Date.now() - cached.at > LATEST_TTL_MS) {
    const value = fetch('/downloads/worker/version.txt', { cache: 'no-cache' })
      .then((r) => (r.ok ? r.text() : ''))
      .then((t) => t.trim() || null)
      .catch(() => ((cached = null), null));
    cached = { at: Date.now(), value };
  }
  return cached.value;
}

/** The worker agent build this server hands out (null when downloads are not set up), refreshed while the page is open. */
export function useLatestWorkerVersion(): string | null {
  return useWorkerBuild().latest;
}

/** The build the server hands out, and whether that has been looked up yet (null + loaded: downloads are not set up). */
export function useWorkerBuild(): { latest: string | null; loaded: boolean } {
  const [state, setState] = useState<{ latest: string | null; loaded: boolean }>({ latest: null, loaded: false });
  useEffect(() => {
    let live = true;
    const load = () => void latestWorkerVersion().then((x) => live && setState({ latest: x, loaded: true }));
    load();
    const timer = setInterval(load, LATEST_TTL_MS);
    window.addEventListener('focus', load);
    return () => {
      live = false;
      clearInterval(timer);
      window.removeEventListener('focus', load);
    };
  }, []);
  return state;
}

export function workerOutdated(version: string, latest: string | null): boolean {
  if (!latest || !isAgentVersion(version)) return false;
  // Older servers cut the reported version to 40 characters, i.e. the commit to 34: a prefix match is the same build.
  const build = version.slice(AGENT_PREFIX.length);
  return build.length < 7 || !latest.startsWith(build);
}

/** Badges for a worker row: an update is on its way, or one is available. */
export function WorkerUpdateBadge({ w, latest }: { w: { version: string; updateRequested: boolean; online: boolean }; latest: string | null }) {
  if (w.updateRequested) return <Badge tone="accent">{w.online ? 'updating…' : 'updates when online'}</Badge>;
  if (workerOutdated(w.version, latest)) return <Badge tone="warn">update available</Badge>;
  return null;
}

/** Why the last self-update failed, under the worker's details. */
export function WorkerUpdateError({ error }: { error: string }) {
  if (!error) return null;
  return (
    <div className="mt-0.5 truncate text-xs text-danger" title={error}>
      Update failed: {error}
    </div>
  );
}
